import { inject, injectable } from 'inversify';
import { serialize } from 'object-to-formdata';

import { Logger } from '../logger';
import { QueryService } from '../query';
import { UpdatorService } from '../updator';
import { AppError } from '../error';

import { RoleService, TokenService, LocaleService } from './types';
import { HTTP_NOT_UNAUTHORIZED_STATUS } from './codes';

const RELOAD_DELAY_AFTER_REQUEST = 400;

type DataType = object;
type QueryType = object;

interface RequestOptions {
  withAuth: boolean;
  withSide: boolean;
  isJson: boolean;
  withIndicies: boolean;
  timeout: number;
  reloadIfOldVersion: boolean;
  role?: 'employee' | 'company';
}

type HeadersRequestOptions = Omit<
  RequestOptions,
  'withIndicies' | 'timeout' | 'reloadIfOldVersion'
>;

const retryOn = (status: number) => {
  return (target: HttpService, key: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;

    descriptor.value = async function (
      resource: RequestInfo,
      options:
        | (RequestInit & { timeout?: number; reloadIfOldVersion?: boolean })
        | undefined = {},
    ) {
      let response = await originalMethod.apply(this, [resource, options]);

      if (response.status !== status) {
        return response;
      }

      const prevToken = target.getToken.apply(this);
      await target.restoreToken.apply(this);
      const nextToken = target.getToken.apply(this);

      if (prevToken !== nextToken) {
        response = await originalMethod.apply(this, [
          resource,
          {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${nextToken}`,
            },
          },
        ]);
      }

      return response;
    };

    return descriptor;
  };
};

@injectable()
export class HttpService {
  private host = window.REACT_APP_VKHRTEK_API_URL;

  private numberRequests = 0;

  private requiredReloadUrlExists = false;

  // eslint-disable-next-line max-params
  constructor(
    private query: QueryService,
    @inject(TokenService) private token: TokenService,
    @inject(RoleService) private role: RoleService,
    @inject(LocaleService)
    private translationLocaleService: LocaleService,
    private logger: Logger,
    private updator: UpdatorService,
  ) {}

  /** Читает body ответа (которое в видете текста JSON-формата) и преобразует к объекту
   * @param {Response} response Интерфес Fetch API представляет собой ответ на запрос
   * @returns {Promise<Record<string, unknown>>}
   *  */
  async parseJson(response: Response): Promise<Record<string, unknown>> {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  /** Расширить объект ответа контекстным значением
   * Для расширения используется Proxy
   * @param {Record<string, unknown>} response Проксируемый/расширяемый объект
   * @param {Record<string, unknown>} context Контекстный объект
   * @returns {Record<string, unknown> & { __context__: Record<string, unknown> }} Проксированный объект с добавленным свойством __context__
   * */
  extendResponse(
    response: Record<string, unknown>,
    context: Record<string, unknown>,
  ) {
    return new Proxy(response, {
      get(target, prop) {
        if (prop === '__context__') {
          return context;
        }
        return target[prop as string];
      },
    }) as Record<string, unknown> & { __context__: Record<string, unknown> };
  }

  /** Базовый метод для выполнения запросов к бэкенду
   * Является оберткой над браузерным fetch
   * Позволяет прервать запрос по истечении timeout
   * Управляет принудительным обновлением страницы в браузере при помощи reloadIfOldVersion из опций
   * @param {string} resource Адрес ресурса
   * @param [options={}] Опции, среди которых часто встречаются body, headers, method, timeout, reloadIfOldVersion
   * @param [log={}]
   * @returns {Promise<Response>}
   *  */
  @retryOn(HTTP_NOT_UNAUTHORIZED_STATUS)
  async fetch(
    resource: string,
    options:
      | (RequestInit & {
          timeout?: number;
          reloadIfOldVersion?: boolean;
          headers?: Record<'X-Side' | 'X-App-Version', string>;
        })
      | undefined = {},
    log: {
      body?: Record<string, unknown>;
    } = {},
  ): Promise<Response> {
    const { timeout = 0, reloadIfOldVersion = false } = options;

    const controller = new AbortController();
    const id = setTimeout(() => {
      if (timeout) {
        controller.abort();

        this.logger.info(
          `[http] [CANCELED BY TIMEOUT] ${
            options.method?.toUpperCase() || 'GET'
          } ${resource.split('?')[0]}`,
          {
            tags: {
              vkdoc_error_type: 'http',
              http_method: options.method?.toUpperCase() || 'GET',
              http_url: resource.split('?')[0],
              http_status: 'canceled',
            },
            context: {
              http_query: resource.split('?')[1]
                ? { value: resource.split('?')[1] }
                : null,
            },
          },
        );
      }
    }, timeout);

    this.numberRequests++;

    if (reloadIfOldVersion) {
      this.requiredReloadUrlExists = true;
    }

    const response = await fetch(resource, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(id);

    const version = response.headers?.get('X-App-Version');

    if (version) {
      this.updator.updateVersion(version);
    }

    this.numberRequests--;

    setTimeout(() => {
      if (this.numberRequests === 0 && this.requiredReloadUrlExists) {
        this.updator.run();
      }
    }, RELOAD_DELAY_AFTER_REQUEST);

    if (!response.ok) {
      let errorResponse: {
        data?: {
          error_code: string;
        };
        code?: number;
        message?: string | string[];
      } = {};

      try {
        errorResponse = await response.clone().json();
      } catch (e) {}

      const traceId = response.headers.get('X-Mayday-Trace-Id');

      this.logger.info(
        `[http] [${response.status}] ${
          options.method?.toUpperCase() || 'GET'
        } ${resource.split('?')[0]}`,
        {
          tags: {
            vkdoc_error_type: 'http',
            http_method: options.method?.toUpperCase() || 'GET',
            http_status: response.status,
            http_url: resource.split('?')[0],
            http_trace_id: traceId || 'empty',
            http_error_code: errorResponse?.data?.error_code || 'empty',
            http_side: options.headers?.['X-Side'] || 'empty',
            http_version: options.headers?.['X-App-Version'] || 'empty',
          },
          context: {
            http_body: log.body
              ? {
                  value: log.body,
                }
              : null,
            http_query: resource.split('?')[1]
              ? { value: resource.split('?')[1] }
              : null,
            http_response: {
              value: errorResponse,
            },
          },
        },
      );

      throw new AppError(
        'server',
        {
          code: errorResponse.code || response.status,
          message: errorResponse.message ?? '',
          data: errorResponse.data,
        },
        traceId,
      );
    }

    return response;
  }

  /** Обновить токен
   * @returns {Promise<void>}
   * */
  async restoreToken() {
    await this.token.restoreToken();
  }

  /** Получить токен
   * Если токен отсутсвует выбросить исключение
   * @returns {string}
   *  */
  getToken(): string {
    const token = this.token.get();

    if (!token) {
      throw new AppError('client', {
        code: 401,
        message: 'Unauthorized',
        error: 'Unauthorized',
      });
    }

    return token;
  }

  /** Получить роль
   * Допустимые роли: employee (сотрудник компании) и company (предстователь со стороны компании)
   * @returns {string}
   *  */
  getRole(): string {
    const role = this.role.get();

    if (!role) {
      return 'employee';
    }

    return role;
  }

  private isValidUrl(url: string) {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Представляет опции для установки заголовков
   * @typedef HeadersRequestOptionsDocs
   * @type {object}
   * @property {boolean} withAuth Определяет будет ли передаваться авторизационный заголовок, содержащий Bearer-токен
   * @property {boolean} withSide Определяет будет ли передаваться заголовок, содержащий роль
   * @property {boolean} isJson Определяет будет ли передаваться заголовок, указывающий тип отправляемых на сервер данных
   */

  /** Сформировать объект заголовков, которые будут отправлены с запросом
   * @param {HeadersRequestOptionsDocs} options
   * @returns {Object.<string, string>}
   * */
  private createHeaders({
    withAuth,
    withSide,
    isJson,
    role,
  }: HeadersRequestOptions): Record<string, string> {
    const headers: { [key: string]: string } = isJson
      ? {
          'Content-Type': 'application/json',
        }
      : {};

    if (withAuth) {
      headers.Authorization = `Bearer ${this.getToken()}`;
    }
    if (withSide) {
      headers['X-Side'] = role || this.getRole();
    }

    headers['Accept-Language'] = this.translationLocaleService.get();

    return headers;
  }

  /** Сформировать тело запроса
   * @param {DataType} [data]
   * @param {boolean} [toJson=true] Определяет требуется ли преобразовать объект данных к JSON-формату
   * @param {boolean} [withIndicies=false]
   * @returns {string | FormData}
   *  */
  private createBody(
    data?: DataType,
    toJson = true,
    withIndicies = false,
  ): string | FormData {
    if (toJson) {
      return data ? JSON.stringify(data) : '';
    }

    return serialize(data, { indices: withIndicies });
  }

  /** Сформировать строку запроса
   * @param {string} url
   * @param {QueryType} [query]
   * @returns {string}
   *  */
  private createQuery(url: string, query?: QueryType) {
    if (!(query && Object.keys(query).length)) {
      return '';
    }

    const symbol = url.includes('?') ? '&' : '?';

    return `${symbol}${this.query.stringify(query)}`;
  }

  /**
   * Представляет допустимые опции для выполнения GET запроса
   * @typedef GetRequestOptionsDocs
   * @type {object}
   * @property {boolean} [withAuth=true]
   * @property {boolean} [withSide=false]
   * @property {boolean} [isJson=true]
   * @property {number} [timeout=0]
   * @property {boolean} [reloadIfOldVersion=false]
   */

  /** Выполнить GET запрос
   * @param {string} url
   * @param {QueryType} [query]
   * @param {GetRequestOptionsDocs} [options={}]
   * */
  async get(
    url: string,
    query?: QueryType,
    {
      withAuth = true,
      withSide = false,
      isJson = true,
      timeout = 0,
      reloadIfOldVersion = false,
      role,
    }: Partial<RequestOptions> = {},
  ): Promise<unknown> {
    const headers = this.createHeaders({
      withAuth,
      withSide,
      isJson,
      role,
    });

    const response = await this.fetch(
      `${this.host}${url}${this.createQuery(url, query)}`,
      {
        headers,
        timeout,
        reloadIfOldVersion,
      },
    );

    return isJson
      ? this.extendResponse(await this.parseJson(response), {
          vkdoc_error_type: 'validation',
          http_url: url,
          http_method: 'GET',
          http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
          http_side: headers['X-Side'] || 'empty',
          http_version: headers['X-App-Version'] || 'empty',
        })
      : null;
  }

  /**
   * Представляет допустимые опции для выполнения GET запроса на загрузку файла
   * @typedef GetFileRequestOptionsDocs
   * @type {object}
   * @property {boolean} [withAuth=true]
   * @property {boolean} [withSide=false]
   * @property {number} [timeout=0]
   */

  /** Выполнить запрос на загрузку файла с сервера
   * @param {string} url
   * @param {GetFileRequestOptionsDocs} [options={}]
   * @param {Record<string, unknown>} [query]
   * @returns {Promise<{ file: Promise<Blob>; filename: string }>}
   *  */
  async getFile(
    url: string,
    {
      withAuth = true,
      withSide = false,
      isJson = false,
      timeout = 0,
      role,
    }: Partial<RequestOptions> = {},
    query?: QueryType,
  ): Promise<{ file: Promise<Blob>; filename: string }> {
    const headers = this.createHeaders({
      withAuth,
      withSide,
      isJson,
      role,
    });

    const fullUrl = this.isValidUrl(url) ? url : `${this.host}${url}`;

    const response = await this.fetch(
      `${fullUrl}${
        query && Object.keys(query).length
          ? `?${this.query.stringify(query)}`
          : ''
      }`,
      {
        headers,
        timeout,
      },
    );

    const contentDisposition =
      response &&
      response.headers &&
      response.headers.get('Content-Disposition');

    const decodedContentDisposition = decodeURI(
      contentDisposition || '',
    )?.replace(/\*|UTF-8|\"|\'\'/g, '');

    const filename = (decodedContentDisposition?.match(/filename=(.+)/) ||
      [])[1];

    return { file: response.blob(), filename: filename };
  }

  /**
   * Представляет допустимые опции для загрузки файла на сервер
   * @typedef UploadFileRequestOptionsDocs
   * @type {object}
   * @property {boolean} [withAuth=true]
   * @property {boolean} [withSide=false]
   * @property {'post'|'put'} [method=post]
   * @property {boolean} [withIndicies=false]
   * @property {number} [timeout=0]
   * @property {boolean} [isJson=false]
   */

  /** Загрузить файл на сервер
   * @param {string} url
   * @param {DataType} [data]
   * @param {UploadFileRequestOptionsDocs} [options={}]
   * @returns {Promise<Blob>}
   *  */
  async generateFile(
    url: string,
    data?: DataType,
    {
      withAuth = true,
      withSide = false,
      method = 'post',
      withIndicies = false,
      timeout = 0,
      isJson = false,
      role,
    }: Partial<RequestOptions> & { method?: 'post' | 'put' } = {},
  ): Promise<Blob> {
    const headers = this.createHeaders({
      withAuth,
      withSide,
      isJson,
      role,
    });

    const response = await this.fetch(`${this.host}${url}`, {
      method: method.toUpperCase(),
      body: this.createBody(data, isJson, withIndicies),
      headers,
      timeout,
    });

    return response.blob();
  }

  /**
   * Представляет допустимые опции для выполнения POST запроса
   * @typedef PostRequestOptionsDocs
   * @type {object}
   * @property {boolean} [withAuth=true]
   * @property {boolean} [withSide=false]
   * @property {boolean} [isJson=true]
   * @property {boolean} [withIndicies=false]
   * @property {number} [timeout=0]
   * @property {boolean} [reloadIfOldVersion=false]
   */

  /**
   * @param {string} url
   * @param {DataType} [data]
   * @param {PostRequestOptionsDocs} [options={}]
   * @returns {Promise<unknown>}
   * */
  async post(
    url: string,
    data?: DataType,
    {
      withAuth = true,
      withSide = false,
      isJson = true,
      withIndicies = false,
      timeout = 0,
      reloadIfOldVersion = false,
      role,
    }: Partial<RequestOptions> = {},
  ): Promise<unknown> {
    const headers = this.createHeaders({
      withAuth,
      withSide,
      isJson,
      role,
    });

    const response = await this.fetch(`${this.host}${url}`, {
      method: 'POST',
      body: this.createBody(data, isJson, withIndicies),
      headers,
      timeout,
      reloadIfOldVersion,
    });

    try {
      const res = this.extendResponse(await this.parseJson(response), {
        vkdoc_error_type: 'validation',
        http_url: url,
        http_method: 'POST',
        http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
        http_side: headers['X-Side'] || 'empty',
        http_version: headers['X-App-Version'] || 'empty',
      });

      return res;
    } catch (e) {
      return null;
    }
  }

  /**
   * Представляет допустимые опции для выполнения PUT запроса
   * @typedef PutRequestOptionsDocs
   * @type {object}
   * @property {boolean} [withAuth=true]
   * @property {boolean} [withSide=false]
   * @property {boolean} [isJson=true]
   * @property {boolean} [withIndicies=false]
   * @property {number} [timeout=0]
   */

  /**
   * @param {string} url
   * @param {DataType} [data]
   * @param {PutRequestOptionsDocs} [options={}]
   * @returns {Promise<unknown>}
   * */
  async put(
    url: string,
    data?: DataType,
    {
      withAuth = true,
      withSide = false,
      isJson = true,
      withIndicies = false,
      timeout = 0,
      role,
    }: Partial<RequestOptions> = {},
  ): Promise<unknown> {
    const headers = this.createHeaders({
      withAuth,
      withSide,
      isJson,
      role,
    });

    const response = await this.fetch(`${this.host}${url}`, {
      method: 'PUT',
      body: this.createBody(data, isJson, withIndicies),
      headers,
      timeout,
    });

    return isJson
      ? this.extendResponse(await this.parseJson(response), {
          vkdoc_error_type: 'validation',
          http_url: url,
          http_method: 'PUT',
          http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
          http_side: headers['X-Side'] || 'empty',
          http_version: headers['X-App-Version'] || 'empty',
        })
      : null;
  }

  /**
   * @param {string} url
   * @param {DataType} [data]
   * @param {PatchRequestOptionsDocs} [options={}]
   * @returns {Promise<unknown>}
   * */
  async patch(
    url: string,
    data?: DataType,
    {
      withAuth = true,
      withSide = false,
      isJson = true,
      withIndicies = false,
      timeout = 0,
      role,
    }: Partial<RequestOptions> = {},
  ): Promise<unknown> {
    const headers = this.createHeaders({
      withAuth,
      withSide,
      isJson,
      role,
    });

    const response = await this.fetch(`${this.host}${url}`, {
      method: 'PATCH',
      body: this.createBody(data, isJson, withIndicies),
      headers,
      timeout,
    });

    return isJson
      ? this.extendResponse(await this.parseJson(response), {
          vkdoc_error_type: 'validation',
          http_url: url,
          http_method: 'PATCH',
          http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
          http_side: headers['X-Side'] || 'empty',
          http_version: headers['X-App-Version'] || 'empty',
        })
      : null;
  }

  /**
   * Представляет допустимые опции для выполнения DELETE запроса
   * @typedef DeleteRequestOptionsDocs
   * @type {object}
   * @property {boolean} [withAuth=true]
   * @property {boolean} [withSide=false]
   * @property {boolean} [isJson=true]
   * @property {boolean} [withIndicies=false]
   * @property {number} [timeout=0]
   */

  /**
   * @param {string} url
   * @param {Record<string, unknown>} [data]
   * @param {DeleteRequestOptionsDocs} [options={}]
   * @returns {Promise<unknown>}
   * */
  async delete(
    url: string,
    data?: DataType,
    {
      withAuth = true,
      withSide = false,
      isJson = true,
      withIndicies = false,
      timeout = 0,
      role,
    }: Partial<RequestOptions> = {},
  ): Promise<unknown> {
    const headers = this.createHeaders({
      withAuth,
      withSide,
      isJson,
      role,
    });

    const response = await this.fetch(`${this.host}${url}`, {
      method: 'DELETE',
      body: this.createBody(data, isJson, withIndicies),
      headers,
      timeout,
    });

    return isJson
      ? this.extendResponse(await this.parseJson(response), {
          vkdoc_error_type: 'validation',
          http_url: url,
          http_method: 'DELETE',
          http_trace_id: response.headers.get('X-Mayday-Trace-Id'),
          http_side: headers['X-Side'] || 'empty',
          http_version: headers['X-App-Version'] || 'empty',
        })
      : null;
  }
}
